/**
 * Invite User Dialog Component
 *
 * Modal dialog for inviting new users to the organization.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useBundleCatalog, useBundlingEnabled } from '@features/subscriptions';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import type { ProviderRoleInfo } from '@store/generated/organizations';
import { getRoleDisplayName, ROLES } from '@utils/roleUtils';
import { useSnackbar } from 'notistack';

import { useCreateInvitationMutation } from '../store/api';

interface InviteUserDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  availableRoles?: ProviderRoleInfo[];
  isTrialing?: boolean;
}

export function InviteUserDialog({
  open,
  onClose,
  onSuccess,
  availableRoles = [],
  isTrialing = false,
}: InviteUserDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { clinicians } = useEventTracking();
  const [createInvitation, { isLoading }] = useCreateInvitationMutation();
  const { isBundlingEnabled: showBundling } = useBundlingEnabled();
  const {
    tiers,
    getCompatibleUserAddOnsForDisplay,
    isLoading: isCatalogLoading,
  } = useBundleCatalog();

  const [email, setEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState<string>(ROLES.CLINICIAN);
  const [selectedTierId, setSelectedTierId] = useState<string>('');
  const [selectedAddOnIds, setSelectedAddOnIds] = useState<Set<string>>(new Set());

  // Bundles are only allowed for clinicians
  const isClinician = selectedRole === ROLES.CLINICIAN;
  const canSelectBundles = showBundling && isClinician;
  const isSingleTier = tiers.length === 1;

  // Auto-select the single tier when only one is available
  useEffect(() => {
    if (isSingleTier && tiers[0] && !selectedTierId) {
      setSelectedTierId(tiers[0].bundle_id);
    }
  }, [isSingleTier, tiers, selectedTierId]);

  // Compute available add-ons based on selected tier (excludes org-wide bundles like data_security)
  const availableAddOns = useMemo(
    () => getCompatibleUserAddOnsForDisplay(selectedTierId || null),
    [getCompatibleUserAddOnsForDisplay, selectedTierId],
  );

  const handleRoleChange = useCallback((newRole: string) => {
    setSelectedRole(newRole);
    // Clear bundle selections when switching away from clinician
    if (newRole !== ROLES.CLINICIAN) {
      setSelectedTierId('');
      setSelectedAddOnIds(new Set());
    }
  }, []);

  // Handle tier change and clear incompatible add-ons
  const handleTierChange = useCallback(
    (newTierId: string) => {
      setSelectedTierId(newTierId);

      // Clear any selected add-ons that are no longer compatible
      if (newTierId) {
        const compatibleAddOns = getCompatibleUserAddOnsForDisplay(newTierId);
        const compatibleIds = new Set(compatibleAddOns.map(a => a.bundle_id));
        setSelectedAddOnIds(prev => {
          const filtered = new Set([...prev].filter(id => compatibleIds.has(id)));
          return filtered.size !== prev.size ? filtered : prev;
        });
      } else {
        // If no tier selected, clear all add-ons
        setSelectedAddOnIds(new Set());
      }
    },
    [getCompatibleUserAddOnsForDisplay],
  );

  const handleToggleAddOn = useCallback((bundleId: string) => {
    setSelectedAddOnIds(prev => {
      const next = new Set(prev);
      if (next.has(bundleId)) {
        next.delete(bundleId);
      } else {
        next.add(bundleId);
      }
      return next;
    });
  }, []);

  const handleInvite = useCallback(async () => {
    if (!email.trim()) {
      enqueueSnackbar('Please enter an email address', { variant: 'warning' });
      return;
    }

    // Collect bundle IDs if bundling is enabled and user is a clinician
    const bundleIds: string[] = [];
    if (canSelectBundles) {
      if (!selectedTierId) {
        enqueueSnackbar('Please select a tier', { variant: 'warning' });
        return;
      }
      bundleIds.push(selectedTierId);
      bundleIds.push(...Array.from(selectedAddOnIds));
    }

    try {
      const result = await createInvitation({
        providerInvitationRequest: {
          email: email.trim(),
          role: selectedRole,
          ...(bundleIds.length > 0 && { bundle_ids: bundleIds }),
        },
      }).unwrap();

      clinicians.inviteSent({
        role: selectedRole,
        has_tier: !!selectedTierId,
        tier_id: selectedTierId || undefined,
        addon_count: selectedAddOnIds.size,
        addon_ids: Array.from(selectedAddOnIds),
        success: true,
        was_resent: result.resent || false,
      });

      const message = result.resent
        ? 'Invitation resent successfully'
        : 'Invitation sent successfully';
      enqueueSnackbar(message, { variant: 'success' });
      setEmail('');
      setSelectedRole(ROLES.CLINICIAN);
      setSelectedTierId('');
      setSelectedAddOnIds(new Set());
      onSuccess();
    } catch (error: any) {
      clinicians.inviteSent({
        role: selectedRole,
        has_tier: !!selectedTierId,
        tier_id: selectedTierId || undefined,
        addon_count: selectedAddOnIds.size,
        addon_ids: Array.from(selectedAddOnIds),
        success: false,
        was_resent: false,
      });
      enqueueSnackbar(error?.data?.message || 'Failed to send invitation', { variant: 'error' });
    }
  }, [
    email,
    selectedRole,
    selectedTierId,
    selectedAddOnIds,
    canSelectBundles,
    createInvitation,
    enqueueSnackbar,
    onSuccess,
  ]);

  const handleClose = useCallback(() => {
    if (!isLoading) {
      setEmail('');
      setSelectedRole(ROLES.CLINICIAN);
      setSelectedTierId('');
      setSelectedAddOnIds(new Set());
      onClose();
    }
  }, [isLoading, onClose]);

  // Get available roles from API or use defaults
  const rolesToDisplay =
    availableRoles.length > 0
      ? availableRoles
      : [
          {
            role: ROLES.CLINICIAN,
            display_name: 'Clinician',
            description: 'Clinical staff member',
          },
          {
            role: ROLES.OPERATIONS_SUPPORT,
            display_name: 'Operations Support',
            description: 'Operations team member',
          },
          {
            role: ROLES.PRACTICE_ADMINISTRATOR,
            display_name: 'Practice Administrator',
            description: 'Practice admin with billing access',
          },
          {
            role: ROLES.BILLING_ADMINISTRATOR,
            display_name: 'Billing Administrator',
            description: 'Billing-only access',
          },
          {
            role: ROLES.SUPERVISOR,
            display_name: 'Supervisor',
            description: 'Clinical supervisor',
          },
        ];

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth={"sm"}>
      <DialogTitle>Invite New User</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField
            autoFocus
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={email}
            onChange={e => setEmail(e.target.value)}
            disabled={isLoading}
            onKeyPress={e => {
              if (e.key === 'Enter' && !isLoading) {
                handleInvite();
              }
            }}
           margin="dense"/>
          <FormControl fullWidth variant="outlined">
            <InputLabel>Role</InputLabel>
            <Select
              value={selectedRole}
              onChange={e => handleRoleChange(e.target.value)}
              label="Role"
              disabled={isLoading}
            >
              {rolesToDisplay.map(role => (
                <MenuItem key={role.role} value={role.role}>
                  {role.display_name || getRoleDisplayName(role.role)}
                  {role.description && (
                    <span
                      style={{ marginLeft: '8px', fontSize: '0.875rem', color: 'text.secondary' }}
                    >
                      - {role.description}
                    </span>
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Bundle Selection - only shown when feature is enabled and role is clinician */}
          {canSelectBundles && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">
                Subscription Bundles
              </Typography>
              {isTrialing && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  You won&apos;t be charged until after your trial ends.
                </Alert>
              )}

              {isCatalogLoading ? (
                // Loading skeleton
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Skeleton variant="rounded"  sx={{ height: 40 }}/>
                  <Box sx={{ minHeight: 135 }}>
                    <Skeleton variant="text"  sx={{ width: 60 }}/>
                    <Skeleton variant="rectangular" sx={{height: 32,  mt: 1 }} />
                    <Skeleton variant="rectangular" sx={{height: 32,  mt: 1 }} />
                    <Skeleton variant="rectangular" sx={{height: 32,  mt: 1 }} />
                  </Box>
                </Box>
              ) : (
                <>
                  {/* Tier Selection */}
                  {isSingleTier ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 1.5,
                        bgcolor: 'grey.50',
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {tiers[0].name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ${(tiers[0].monthly_unit_amount / 100).toFixed(2)}/mo
                      </Typography>
                    </Box>
                  ) : (
                    <FormControl fullWidth variant="outlined" size="small" required>
                      <InputLabel>Tier</InputLabel>
                      <Select
                        value={selectedTierId}
                        onChange={e => handleTierChange(e.target.value)}
                        label="Tier"
                        disabled={isLoading}
                      >
                        {tiers.map(tier => (
                          <MenuItem key={tier.bundle_id} value={tier.bundle_id}>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                width: '100%',
                              }}
                            >
                              <span>{tier.name}</span>
                              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                                ${(tier.monthly_unit_amount / 100).toFixed(2)}/mo
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {/* Add-ons Selection */}
                  <Box sx={{ minHeight: 135 }}>
                    {selectedTierId && availableAddOns.length > 0 && (
                      <FormControl component="fieldset" variant="standard">
                        <FormLabel component="legend" sx={{ fontSize: '0.875rem' }}>
                          Add-ons
                        </FormLabel>
                        <FormGroup>
                          {availableAddOns.map(addOn => (
                            <FormControlLabel
                              key={addOn.bundle_id}
                              control={
                                <Checkbox
                                  checked={selectedAddOnIds.has(addOn.bundle_id)}
                                  onChange={() => handleToggleAddOn(addOn.bundle_id)}
                                  disabled={isLoading}
                                  size="small"
                                />
                              }
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <span>{addOn.name}</span>
                                  <Typography variant="caption" color="text.secondary">
                                    ${(addOn.monthly_unit_amount / 100).toFixed(2)}/mo
                                  </Typography>
                                </Box>
                              }
                            />
                          ))}
                        </FormGroup>
                      </FormControl>
                    )}
                  </Box>
                </>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleInvite}
          color="primary"
          variant="contained"
          disabled={isLoading || !email.trim() || (canSelectBundles && !selectedTierId)}
          startIcon={isLoading ? <CircularProgress size={16} /> : null}
        >
          {isLoading ? 'Sending...' : 'Send Invite'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
